import Button from "@src/components/Button/Button";
import Input from "@src/components/Input/Input";
import Logo from "@src/components/Logo/Logo";
import * as yup from "yup";
//@ts-ignore
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { useUserStore } from "@src/stores/userStore";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import useRedirectIfAuthenticated from "@src/hooks/useRedirectIfAuthenticated";
import FormField from "@src/components/FormField/FormField";
import Hint from "@src/components/Helpers/Hint/Hint";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import Page from "@src/layout/Page";

const schema = yup.object().shape({
    email: yup
        .string()
        .email("Please check that you typed a correct email")
        .required(),
    password: yup
        .string()
        .required("Password required")
        .min(12, "Password must be at least 12 characters")
        .matches(
            /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{12,}$/,
            {
                message:
                    "Password must be 12 characters long, at least one upper case letter, one lower case letter, one number and one special character",
            },
        ),
    confirm_password: yup
        .string()
        .required("Please confirm your password")
        .oneOf([yup.ref("password")], "Passwords must match"),
    portfolio_link: yup.string().url().required(),
    username: yup.string().max(200),
    captcha: yup
        .boolean()
        .test({
            name: "is captcha valid",
            message: "must be less than ${max} characters",
            test: (value) => value,
        })
        .required("You failed to pass the captcha"),
    terms_and_conditions: yup
        .boolean()
        .test({
            name: "T&Cs accepted",
            message:
                "You must accept the terms and conditions to create an account.",
            test: (value) => {
                console.log(value);
                return value;
            },
        })
        .required(),
});
const Register = () => {
    interface FormValues extends yup.InferType<typeof schema> { }
    const { register_user, token } = useUserStore();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState<number>(1);
    const {
        register,
        setValue,
        handleSubmit,
        setError,
        formState: { errors },
        getValues,
        watch,
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
    });
    const watcher = watch("terms_and_conditions");

    useEffect(() => {
        console.log(watcher);
    }, [watcher]);
    const navigate = useNavigate();

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        setLoading(true);
        const res = await register_user(data);
        if (res !== undefined) {
            Object.entries(res || {}).forEach((errors: [string, string[]]) => {
                const [key, values] = errors;
                values.forEach((value) => {
                    //@ts-expect-error Expects "email"|"password"|"confirm_password"
                    setError(key, {
                        type: "custom",
                        message: value,
                    });
                });
            });
        }
        setLoading(false);
    };
    function handleVerificationSuccess() {
        setValue("captcha", true);
    }
    useEffect(() => {
        localStorage.setItem("token", token!);
        useRedirectIfAuthenticated(navigate);
    }, [token]);

    return (
        <Page>
            <Logo size="m" />
            <div className="wrapper">

                <h2>Create an account </h2>

                <div className="form-container">
                    {step === 1 && (
                        <form className="step1">
                            <FormField errors={errors} name="email">
                                <Hint title="Hey, you, psst">
                                    <p>
                                        Have you considered using an alias instead
                                        of your actual e-mail?
                                    </p>
                                    <p>
                                        Try as I might, no data is ever safe on the
                                        web and I am just one developer.
                                    </p>
                                    <p>
                                        By using an alias, you'll be able to receive
                                        e-mails while never disclosing your real
                                        e-mail address. This means if Charsh'
                                        database gets compromised, you won't get
                                        spammed.
                                    </p>
                                    <p>
                                        I personally use SimpleLogin since it's
                                        bundled with my e-mail service but I'm sure
                                        there are plenty of other options out there.
                                        Just remember to keep the important stuff
                                        linked to an actual e-mail and never
                                        associate an alias to an account that might
                                        hold sensitive information! Consider it as a
                                        spambox !
                                    </p>
                                </Hint>
                                <Input
                                    type="text"
                                    name="email"
                                    placeholder="Your e-mail"
                                    register={register}
                                    required
                                />
                            </FormField>
                            <FormField name="password" errors={errors}>
                                <Input
                                    type="password"
                                    name="password"
                                    placeholder="Your password"
                                    register={register}
                                    required
                                />
                            </FormField>

                            <FormField
                                name="confirm_password"
                                errors={errors}
                                hideLabel={true}
                            >
                                <label
                                    htmlFor="confirm_password"
                                    className="input__label"
                                >
                                    Confirm your password
                                </label>
                                <Input
                                    type="password"
                                    name="confirm_password"
                                    placeholder="Confirm password"
                                    register={register}
                                    required
                                />
                            </FormField>

                            <Button
                                preset="secondary"
                                type="button"
                                fn={() => setStep((old) => old + 1)}
                                isLoading={loading}
                            >
                                Next
                            </Button>
                        </form>
                    )}
                    {step === 2 && (
                        <>
                            <form
                                className="step2"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <FormField
                                    errors={errors}
                                    name="username"
                                    hideLabel={true}
                                >
                                    <label
                                        htmlFor="username"
                                        className="input__label"
                                    >
                                        Username (Optional)
                                        <br />
                                        <span className="text-info">
                                            This is what people will use to add you
                                            to their friendlist and what I call you
                                        </span>
                                    </label>
                                    <Input
                                        type="text"
                                        name="username"
                                        placeholder="Your username"
                                        register={register}
                                    />
                                </FormField>
                                <FormField
                                    name="portfolio_link"
                                    errors={errors}
                                    hideLabel={true}
                                >
                                    <label
                                        htmlFor="portfolio_link"
                                        className="input__label"
                                    >
                                        Link to social*
                                        <br />
                                        <span className="text-info">
                                            Twitter, Toyhou.se, bsky, DA, FA, you
                                            name it.
                                        </span>
                                    </label>
                                    <Input
                                        name="portfolio_link"
                                        register={register}
                                        required
                                    />
                                </FormField>
                                <FormField
                                    name="terms_and_conditions"
                                    hideLabel={true}
                                    errors={errors}
                                >
                                    <Input
                                        type="checkbox"
                                        name="terms_and_conditions"
                                        label="I am 18 years old or over and I accept the "
                                        register={register}
                                    />
                                    <span className="t-and-c"><Link target="_blank" to="/terms-and-conditions">terms and conditions.</Link></span>
                                </FormField>
                                <HCaptcha
                                    sitekey={import.meta.env.VITE_HCAPTCHA_KEY}
                                    onVerify={() => handleVerificationSuccess()}
                                />
                                <div className="button-container">
                                    <Button
                                        preset="secondary"
                                        type="button"
                                        fn={() => setStep((old) => old - 1)}
                                        isLoading={loading}
                                    >
                                        Back
                                    </Button>

                                    <Button
                                        preset="primary"
                                        type="submit"
                                        isLoading={loading}
                                    >
                                        Register
                                    </Button>
                                </div>
                            </form>

                            <Hint title="* Why do you want this?">
                                <p>
                                    In order to assess and ensure that your
                                    characters are your own and that no thievery is
                                    happening in my domain, I’ll personally review
                                    the social account given here.
                                </p>
                                <p>
                                    It will NOT be displayed anywhere. If needed, I
                                    might contact you through DMs if available on
                                    the platform. If I find anything odd, your
                                    access will be limited and you’ll receive a
                                    notification so we can clear things up.
                                </p>
                                <p>
                                    Don’t worry I’m (probably) as much as an
                                    introvert as you are.
                                </p>
                            </Hint>
                        </>
                    )}
                </div>
            </div>
        </Page>
    );
};
export default Register;
